import { NO_CHEATS, parseCheats, type Cheats } from "./cheats";
import type { Button } from "./input";
import { parseHintsSeen, type HintId } from "./ride-hints";
import type { RideId } from "./sim/rides";
import { parseSpecialSeqs } from "./special-seqs";
import { DEFAULT_SPECIAL_SEQS, type SpecialSeqs } from "./tricks";

// Player settings (#36): button remapping on the pad and the keyboard, a
// camera preset, the art style (#75) and the volume. Sim-free and render-free: `InputManager`
// reads the bindings, Game the camera and volume, React draws the Settings
// screen. Saved to localStorage when the page is allowed to (not in some
// sandboxed embeds), so everything here is also fine without it.

/** The actions you can remap. Steering, Start / Select and the fixed keys stay put. */
export const REMAPPABLE = ["ollie", "flip", "grab", "grind", "spinL", "spinR", "revert", "nollie"] as const satisfies readonly Button[];
export type Action = (typeof REMAPPABLE)[number];

export const ACTION_LABELS: Record<Action, string> = {
  ollie: "Ollie",
  flip: "Flip",
  grab: "Grab",
  grind: "Grind",
  spinL: "Spin left",
  spinR: "Spin right",
  revert: "Revert",
  nollie: "Nollie",
};

export type CameraPreset = "close" | "normal" | "far";
export const CAMERA_PRESETS: readonly CameraPreset[] = ["close", "normal", "far"];

/** How far behind and above the skater the chase camera sits, on flat and on vert. */
export interface CameraRig {
  dist: number;
  height: number;
  vertDist: number;
  vertHeight: number;
}

export const CAMERA_RIGS: Record<CameraPreset, CameraRig> = {
  close: { dist: 4.2, height: 1.6, vertDist: 6.2, vertHeight: 2.1 },
  normal: { dist: 5.2, height: 1.9, vertDist: 7.5, vertHeight: 2.5 },
  far: { dist: 6.6, height: 2.5, vertDist: 9.2, vertHeight: 3.1 },
};

export const CAMERA_LABELS: Record<CameraPreset, string> = { close: "Close", normal: "Normal", far: "Far & high" };
/** A line under each camera in the HUD's quick settings (#117). */
export const CAMERA_DETAILS: Record<CameraPreset, string> = {
  close: "Tight on the rider: every flip up close.",
  normal: "A chase view from behind the rider.",
  far: "Pulled back and up: more of the park ahead.",
};

/**
 * The on-screen stick and buttons (#67). Auto shows them on a touch-only
 * device, or once a finger lands on the park (a laptop with a touchscreen);
 * On shows them everywhere, Off never.
 */
export type TouchMode = "auto" | "on" | "off";
export const TOUCH_MODES: readonly TouchMode[] = ["auto", "on", "off"];
export const TOUCH_MODE_LABELS: Record<TouchMode, string> = { auto: "Auto", on: "On", off: "Off" };

export type TouchSize = "S" | "M" | "L";
export const TOUCH_SIZES: readonly TouchSize[] = ["S", "M", "L"];
/** How much bigger than the default each size draws the buttons and the stick. */
export const TOUCH_SIZE_SCALE: Record<TouchSize, number> = { S: 0.82, M: 1, L: 1.18 };

/**
 * Steering sensitivity on the touch stick (#226): how far a drag goes before
 * full lock (`throw`, in thumb units: the stick's radius, `touchLayout`'s
 * `u`) and how eased the response is (`curve`: the push is raised to this
 * power, so it's gentle near the centre and full at the rim). High is
 * nearest the old feel (1.1 units, linear); Medium is the default.
 * #242: #226's first numbers (2.2 / 1.6 for Medium) left a drag that used
 * to be full lock at about a fifth of it, "way too low"; Medium now gives
 * about two thirds there and full lock by 1.45 units.
 */
export type TouchSteer = "low" | "medium" | "high";
export const TOUCH_STEERS: readonly TouchSteer[] = ["low", "medium", "high"];
export const TOUCH_STEER_LABELS: Record<TouchSteer, string> = { low: "Low", medium: "Medium", high: "High" };
export const TOUCH_STEER_FEEL: Record<TouchSteer, { throw: number; curve: number }> = {
  low: { throw: 1.8, curve: 1.4 },
  medium: { throw: 1.45, curve: 1.2 },
  high: { throw: 1.15, curve: 1.05 },
};

/** Button opacity in tenths: 10 is as drawn, down to 3. */
export const TOUCH_OPACITY_MIN = 3;
export const TOUCH_OPACITY_MAX = 10;

export interface TouchSettings {
  mode: TouchMode;
  size: TouchSize;
  opacity: number;
  /** Left-handed: the buttons on the left, the stick on the right. */
  left: boolean;
  /** Steering sensitivity (#226). */
  steer: TouchSteer;
}

/**
 * The art style (#75): how the park and the rider look. The looks themselves
 * live in `render/styles/` (one `ArtStyle` each); this is only their ids and
 * names, so settings stay render-free.
 */
export const ART_STYLES = ["cartoon", "realistic", "retro", "comic", "clay"] as const;
export type ArtStyleId = (typeof ART_STYLES)[number];
export const ART_STYLE_LABELS: Record<ArtStyleId, string> = { cartoon: "Cartoon", realistic: "Realistic", retro: "Retro", comic: "Comic", clay: "Clay" };

export function isArtStyle(v: unknown): v is ArtStyleId {
  return ART_STYLES.includes(v as ArtStyleId);
}

/**
 * Render quality (#75): High draws a style's ambient occlusion and bloom and
 * its full shadow resolution; Low skips both passes and halves the shadow
 * map. Auto is Low on a touch-only device (a phone or tablet) and High
 * otherwise.
 */
export const QUALITIES = ["auto", "low", "high"] as const;
export type Quality = (typeof QUALITIES)[number];
export const QUALITY_LABELS: Record<Quality, string> = { auto: "Auto", low: "Low", high: "High" };
/** A line under each choice in Pause > Settings > Quality. */
export const QUALITY_DETAILS: Record<Quality, string> = {
  auto: "Low on a phone or tablet, High everywhere else.",
  low: "No ambient occlusion or bloom, half-size shadows. Smoothest.",
  high: "The style's full post effects and shadows.",
};

/** What Auto means on this device. */
export function effectiveQuality(q: Quality, touchOnly: boolean): "low" | "high" {
  return q === "auto" ? (touchOnly ? "low" : "high") : q;
}

/**
 * The cosmetic lists (bail style and the trick effects) can also be set to
 * Random (#123): a different pick from the list every time one plays (#128;
 * it was once per load), and what a new player starts on, so they see the
 * fun ones without digging through Settings. `CosmeticPicker` makes the
 * picks.
 */
export const RANDOM = "random";
export type Random = typeof RANDOM;
export const RANDOM_LABEL = "Random";
export const RANDOM_DETAIL = "A different one every time.";
/** A cosmetic list's choices: Random first, then the list. */
const withRandom = <T extends string>(ids: readonly T[]): readonly (T | Random)[] => [RANDOM, ...ids];

/**
 * How a bail looks (#83): render-only, so the sim, the bail's length and the
 * respawn are the same whichever you pick. `render/bail-fx.ts` plays them.
 */
export const BAIL_STYLES = ["classic", "explode", "confetti", "fireworks"] as const;
export type BailStyleId = (typeof BAIL_STYLES)[number];
export const BAIL_STYLE_CHOICES = withRandom(BAIL_STYLES);
export const BAIL_STYLE_LABELS: Record<BailStyleId | Random, string> = { random: RANDOM_LABEL, classic: "Classic", explode: "Explode", confetti: "Confetti", fireworks: "Fireworks" };
/** A line under each bail style in the HUD's quick settings (#117). */
export const BAIL_STYLE_DETAILS: Record<BailStyleId | Random, string> = {
  random: RANDOM_DETAIL,
  classic: "The tumble, nothing else.",
  explode: "Limbs and board fly off and bounce around until you respawn.",
  confetti: "The rider bursts into confetti in their colors, with a party horn.",
  fireworks: "You become the fireworks: a rocket shoots up from each part of you and bursts overhead in its colors.",
};

/**
 * Trick effects (#118): particles on grinds and landings. Render-only like
 * the bail styles, so no choice changes the sim. `render/trick-fx.ts` plays
 * them.
 */
export const GRIND_SPARKS = ["classic", "rainbow", "fire", "electric"] as const;
export type GrindSparksId = (typeof GRIND_SPARKS)[number];
export const GRIND_SPARKS_CHOICES = withRandom(GRIND_SPARKS);
export const GRIND_SPARKS_LABELS: Record<GrindSparksId | Random, string> = { random: RANDOM_LABEL, classic: "Classic", rainbow: "Rainbow", fire: "Fire", electric: "Electric" };
/** A line under each choice in the HUD's quick settings, like the bail styles'. */
export const GRIND_SPARKS_DETAILS: Record<GrindSparksId | Random, string> = {
  random: RANDOM_DETAIL,
  classic: "Orange sparks off the trucks.",
  rainbow: "Sparks that cycle through every color.",
  fire: "Embers that float up behind you.",
  electric: "A fast blue-white crackle.",
};
export const LANDING_FX = ["dust", "ring", "splat", "notes"] as const;
export type LandingFxId = (typeof LANDING_FX)[number];
export const LANDING_FX_CHOICES = withRandom(LANDING_FX);
export const LANDING_FX_LABELS: Record<LandingFxId | Random, string> = { random: RANDOM_LABEL, dust: "Dust puff", ring: "Shockwave", splat: "Paint splat", notes: "Music notes" };
export const LANDING_FX_DETAILS: Record<LandingFxId | Random, string> = {
  random: RANDOM_DETAIL,
  dust: "A puff of dust around the wheels.",
  ring: "A ring races out along the ground; a Perfect adds a gold one.",
  splat: "Paint splats where you touch down.",
  notes: "Music notes float up off the rider.",
};
export const AIR_TRAILS = ["off", "ribbon", "comet", "speed"] as const;
export type AirTrailId = (typeof AIR_TRAILS)[number];
export const AIR_TRAIL_CHOICES = withRandom(AIR_TRAILS);
export const AIR_TRAIL_LABELS: Record<AirTrailId | Random, string> = { random: RANDOM_LABEL, off: "Off", ribbon: "Ribbon", comet: "Comet tail", speed: "Speed lines" };
export const AIR_TRAIL_DETAILS: Record<AirTrailId | Random, string> = {
  random: RANDOM_DETAIL,
  off: "Nothing behind the board.",
  ribbon: "A white ribbon through the board's path.",
  comet: "Glowing bits left hanging behind the board.",
  speed: "Comic speed lines around the rider.",
};
/**
 * What a big combo's landing sets off: fireworks, a crowd cheer (a sound), or
 * a slow-mo zoom (the camera punches in and the effects slow down; the sim
 * doesn't, so nothing you skate changes).
 */
export const FINISHERS = ["fireworks", "cheer", "slowmo", "off"] as const;
export type FinisherId = (typeof FINISHERS)[number];
export const FINISHER_CHOICES = withRandom(FINISHERS);
export const FINISHER_LABELS: Record<FinisherId | Random, string> = { random: RANDOM_LABEL, fireworks: "Fireworks", cheer: "Crowd cheer", slowmo: "Slow-mo zoom", off: "Off" };
export const FINISHER_DETAILS: Record<FinisherId | Random, string> = {
  random: RANDOM_DETAIL,
  fireworks: "Three bursts in party colors ahead of you.",
  cheer: "The crowd goes wild.",
  slowmo: "The camera punches in and the landing hangs in slow motion.",
  off: "Nothing extra.",
};

/** The ride you start on (#174): picked in the lobby, changeable in Settings. */
export const RIDE_CHOICES = ["board", "bmx", "scooter", "skates"] as const satisfies readonly RideId[];
export const RIDE_LABELS: Record<RideId, string> = { board: "Board", bmx: "BMX", scooter: "Scooter", skates: "Roller skates" };
export const RIDE_DETAILS: Record<RideId, string> = {
  board: "Flips, grabs and grinds: the classic.",
  bmx: "Bigger hops, tailwhips and peg grinds.",
  scooter: "Deck whips and quick spins.",
  skates: "No stance to swap: spin and flip the body.",
};

/** Which Settings screen a row is on (#126): the look, the trick effects, or sound and camera. */
export type SettingGroup = "look" | "effects" | "soundCamera";

/**
 * A pick-one setting (#132): everything the menus, the quick settings,
 * loading and Random need to know about it. `SETTING_ROWS` has one per
 * setting; the step functions, `parseSettings`'s checks, the pause rows and
 * their actions, the overlay's labels, the quick-settings lists and the
 * Random pools all come from it. Adding one = its ids, labels and details
 * above, its field in `Settings` and `defaultSettings`, and a row here.
 */
export interface SettingRow<V extends string = string> {
  /** The row's name in Pause > Settings, and its list's title in the quick settings. */
  label: string;
  group: SettingGroup;
  /** "step": ←/→ (and X) step through it on the row. "list": X opens a list of every choice, and only a pick applies (a rebuild for the look). */
  kind: "step" | "list";
  /** A list row's pick actions are `${pick}:${id}`. */
  pick?: string;
  /** Its choices, Random aside. */
  ids: readonly V[];
  /** Each choice's name (Random's too, when it has one). */
  labels: Readonly<Record<string, string>>;
  /** A line under each choice; the art style's pitch lives with the look instead (`render/styles`). */
  details?: Readonly<Record<string, string>>;
  /** Random applies (#123): it's the first choice and a new player's pick, and `CosmeticPicker` picks it each time one plays. */
  random: boolean;
  /** It's in the HUD's quick settings (#117). */
  quick: boolean;
}

/** The pick-one settings, in Pause > Settings order. Volume, the touch rows, bindings and cheats work their own way. */
export const SETTING_ROWS = {
  camera: { label: "Camera", group: "soundCamera", kind: "step", ids: CAMERA_PRESETS, labels: CAMERA_LABELS, details: CAMERA_DETAILS, random: false, quick: true },
  artStyle: { label: "Art style", group: "look", kind: "list", pick: "style", ids: ART_STYLES, labels: ART_STYLE_LABELS, random: false, quick: true },
  quality: { label: "Quality", group: "look", kind: "list", pick: "quality", ids: QUALITIES, labels: QUALITY_LABELS, details: QUALITY_DETAILS, random: false, quick: false },
  bailStyle: { label: "Bail style", group: "effects", kind: "list", pick: "bailStyle", ids: BAIL_STYLES, labels: BAIL_STYLE_LABELS, details: BAIL_STYLE_DETAILS, random: true, quick: true },
  grindSparks: { label: "Grind sparks", group: "effects", kind: "list", pick: "grindSparks", ids: GRIND_SPARKS, labels: GRIND_SPARKS_LABELS, details: GRIND_SPARKS_DETAILS, random: true, quick: true },
  landingFx: { label: "Landing effect", group: "effects", kind: "list", pick: "landingFx", ids: LANDING_FX, labels: LANDING_FX_LABELS, details: LANDING_FX_DETAILS, random: true, quick: true },
  airTrail: { label: "Air trail", group: "effects", kind: "list", pick: "airTrail", ids: AIR_TRAILS, labels: AIR_TRAIL_LABELS, details: AIR_TRAIL_DETAILS, random: true, quick: true },
  ride: { label: "Default ride", group: "soundCamera", kind: "step", ids: RIDE_CHOICES, labels: RIDE_LABELS, details: RIDE_DETAILS, random: false, quick: false },
  finisher: { label: "Big combo finisher", group: "effects", kind: "list", pick: "finisher", ids: FINISHERS, labels: FINISHER_LABELS, details: FINISHER_DETAILS, random: true, quick: true },
} as const satisfies { readonly [K in ChoiceKey]: SettingRow<Exclude<Settings[K], Random>> };

type Rows = typeof SETTING_ROWS;
/** The pick-one settings' keys. */
export type ChoiceKey = "camera" | "artStyle" | "quality" | "bailStyle" | "grindSparks" | "landingFx" | "airTrail" | "ride" | "finisher";
export const CHOICE_KEYS = Object.keys(SETTING_ROWS) as ChoiceKey[];
/** The rows ←/→ steps, and the ones that open a list. */
export type StepKey = { [K in ChoiceKey]: Rows[K]["kind"] extends "step" ? K : never }[ChoiceKey];
export type ListKey = Exclude<ChoiceKey, StepKey>;
/** A list row's pick action: `style:clay`, `quality:low`. */
export type PickAction = { [K in ListKey]: `${Rows[K]["pick"]}:${Settings[K]}` }[ListKey];

export function isListKey(v: string): v is ListKey {
  return v in SETTING_ROWS && SETTING_ROWS[v as ChoiceKey].kind === "list";
}

/** Every choice of a setting, in order: Random first where it applies. */
export const SETTING_CHOICES = Object.fromEntries(
  CHOICE_KEYS.map((k) => [k, SETTING_ROWS[k].random ? withRandom<string>(SETTING_ROWS[k].ids) : SETTING_ROWS[k].ids]),
) as unknown as { readonly [K in ChoiceKey]: readonly Settings[K][] };

/** A choice's name. */
export function choiceLabel<K extends ChoiceKey>(key: K, value: Settings[K]): string {
  const labels: Readonly<Record<string, string>> = SETTING_ROWS[key].labels;
  return labels[value]!;
}

/** The cosmetic settings Random can pick for (#123); how the game runs and reads (style, quality, camera, sound, controls) stays put. */
export type Cosmetic = { [K in ChoiceKey]: Rows[K]["random"] extends true ? K : never }[ChoiceKey];
export const COSMETICS = CHOICE_KEYS.filter((k) => SETTING_ROWS[k].random) as Cosmetic[];

/** One pick from each cosmetic list. */
export type CosmeticPick = { [K in Cosmetic]: Rows[K]["ids"][number] };

/** What Random picks from. Every choice for now, defaults included; once unlocks exist, only what the player has unlocked. */
export const COSMETIC_POOLS = Object.fromEntries(COSMETICS.map((k) => [k, SETTING_ROWS[k].ids])) as unknown as {
  readonly [K in Cosmetic]: readonly CosmeticPick[K][];
};

/**
 * Plays Random (#128): a fresh pick from the pool every time an effect
 * plays (each bail, grind, landing, air, big combo), never the one it
 * picked last, so it feels random. Render-only: `rng` is `Math.random` in
 * the game (seeded in tests), never the sim's, so trajectories don't move.
 */
export class CosmeticPicker {
  private readonly last: Partial<Record<Cosmetic, string>> = {};

  constructor(
    private readonly rng: () => number,
    private readonly pools = COSMETIC_POOLS,
  ) {}

  /** A pick for `key`, other than its last one when the pool has another. */
  next<K extends Cosmetic>(key: K): CosmeticPick[K] {
    const pool = this.pools[key];
    const skip = pool.length > 1 ? pool.indexOf(this.last[key] as CosmeticPick[K]) : -1;
    const n = skip < 0 ? pool.length : pool.length - 1;
    let i = Math.min(n - 1, Math.floor(this.rng() * n));
    if (skip >= 0 && i >= skip) i++;
    const pick = pool[i]!;
    this.last[key] = pick;
    return pick;
  }

  /** A cosmetic setting's `value` as it plays this time: itself, or a fresh pick when it's Random. */
  play<K extends Cosmetic>(key: K, value: CosmeticPick[K] | Random): CosmeticPick[K] {
    return value === RANDOM ? this.next(key) : value;
  }
}

/** Volume steps: 0 is off, 10 full. */
export const VOLUME_MAX = 10;

export interface Settings {
  volume: number;
  camera: CameraPreset;
  artStyle: ArtStyleId;
  quality: Quality;
  /** The cosmetics (#123): a pick, or Random (see `CosmeticPicker`). */
  bailStyle: BailStyleId | Random;
  grindSparks: GrindSparksId | Random;
  landingFx: LandingFxId | Random;
  airTrail: AirTrailId | Random;
  finisher: FinisherId | Random;
  /** The ride you start on (#174); the pack still swaps rides while skating. */
  ride: RideId;
  /** Standard-mapping button index for each action. */
  pad: Record<Action, number>;
  /** `KeyboardEvent.code`s for each action (the first is the one shown). */
  keys: Record<Action, string[]>;
  touch: TouchSettings;
  /** Cheats (#119): Pause > Settings > Cheats. */
  cheats: Cheats;
  /** Each special's two stick taps (#135): Pause > Settings > Controls > Specials. */
  specials: SpecialSeqs;
  /** One-time hints already shown (#150), so they never show again. */
  hintsSeen: HintId[];
}

/** THPS on a PlayStation pad: ✕ ollie, □ flip, ○ grab, △ grind, L1 / R1 spin, R2 revert, L2 nollie. */
export const DEFAULT_PAD: Readonly<Record<Action, number>> = {
  ollie: 0,
  grab: 1,
  flip: 2,
  grind: 3,
  spinL: 4,
  spinR: 5,
  nollie: 6,
  revert: 7,
};

export const DEFAULT_KEYS: Readonly<Record<Action, readonly string[]>> = {
  ollie: ["Space"],
  flip: ["KeyJ"],
  grab: ["KeyK"],
  grind: ["KeyL", "KeyI"],
  spinL: ["KeyQ"],
  spinR: ["KeyE"],
  revert: ["KeyO"],
  nollie: ["KeyU"],
};

/** Select, Start and the D-pad (steering) can't be given to an action. */
export const RESERVED_PAD: readonly number[] = [8, 9, 12, 13, 14, 15];
/** Steering, pause, help and reset keys can't be given to an action. */
export const RESERVED_KEYS: readonly string[] = [
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "Escape",
  "KeyP",
  "KeyH",
  "KeyR",
];

export function defaultSettings(): Settings {
  return {
    volume: VOLUME_MAX,
    camera: "normal",
    artStyle: "cartoon",
    quality: "auto",
    // A new player starts every cosmetic on Random (#123); saved picks stay.
    bailStyle: RANDOM,
    grindSparks: RANDOM,
    landingFx: RANDOM,
    airTrail: RANDOM,
    finisher: RANDOM,
    ride: "board",
    pad: { ...DEFAULT_PAD },
    keys: Object.fromEntries(REMAPPABLE.map((a) => [a, [...DEFAULT_KEYS[a]]])) as Record<Action, string[]>,
    touch: { mode: "auto", size: "M", opacity: TOUCH_OPACITY_MAX, left: false, steer: "medium" },
    cheats: NO_CHEATS,
    specials: DEFAULT_SPECIAL_SEQS,
    hintsSeen: [],
  };
}

/** Gives `action` pad button `index`; whichever action had it takes `action`'s old one, so nothing is left unbound. */
export function bindPad(s: Settings, action: Action, index: number): Settings {
  const pad = { ...s.pad };
  const other = REMAPPABLE.find((a) => a !== action && pad[a] === index);
  if (other) pad[other] = pad[action];
  pad[action] = index;
  return { ...s, pad };
}

/** Gives `action` key `code` (alone); an action that loses its last key takes `action`'s old keys. */
export function bindKey(s: Settings, action: Action, code: string): Settings {
  const keys = Object.fromEntries(REMAPPABLE.map((a) => [a, s.keys[a].filter((k) => k !== code)])) as Record<Action, string[]>;
  const orphan = REMAPPABLE.find((a) => a !== action && keys[a].length === 0);
  if (orphan) keys[orphan] = s.keys[action].filter((k) => k !== code);
  keys[action] = [code];
  return { ...s, keys };
}

/** Whether any binding differs from the THPS default. */
export function remapped(s: Settings): boolean {
  return REMAPPABLE.some((a) => s.pad[a] !== DEFAULT_PAD[a] || s.keys[a].join() !== DEFAULT_KEYS[a].join());
}

/** One step of the volume (-1 or +1), clamped. */
export function stepVolume(s: Settings, dir: -1 | 1): Settings {
  return { ...s, volume: Math.max(0, Math.min(VOLUME_MAX, s.volume + dir)) };
}

/** The next (or previous) entry of `list` after `v`, wrapping. */
function cycle<T>(list: readonly T[], v: T, dir: -1 | 1): T {
  const n = list.length;
  return list[(list.indexOf(v) + dir + n) % n]!;
}

/** The next (or previous) choice of a pick-one setting, wrapping. */
export function stepChoice(s: Settings, key: ChoiceKey, dir: -1 | 1): Settings {
  return { ...s, [key]: cycle<string>(SETTING_CHOICES[key], s[key], dir) };
}

export const stepCamera = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "camera", dir);
export const stepArtStyle = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "artStyle", dir);
export const stepQuality = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "quality", dir);
export const stepBailStyle = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "bailStyle", dir);
export const stepGrindSparks = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "grindSparks", dir);
export const stepLandingFx = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "landingFx", dir);
export const stepAirTrail = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "airTrail", dir);
export const stepFinisher = (s: Settings, dir: -1 | 1): Settings => stepChoice(s, "finisher", dir);

/**
 * The settings the HUD's quick settings pick from (#117), each a list of
 * choices: art style, bail style, camera, and the four trick effects
 * (#118), which share one icon. The rows marked `quick`.
 */
export type QuickSetting = { [K in ChoiceKey]: Rows[K]["quick"] extends true ? K : never }[ChoiceKey];
export const QUICK_SETTINGS = CHOICE_KEYS.filter((k) => SETTING_ROWS[k].quick) as QuickSetting[];

/** The settings with `key` set to `value`, or null if that's what it already is (so a pick applies once). */
export function pickQuick<K extends QuickSetting>(s: Settings, key: K, value: Settings[K]): Settings | null {
  return s[key] === value ? null : { ...s, [key]: value };
}

export function stepTouchMode(s: Settings, dir: -1 | 1): Settings {
  return { ...s, touch: { ...s.touch, mode: cycle(TOUCH_MODES, s.touch.mode, dir) } };
}

export function stepTouchSize(s: Settings, dir: -1 | 1): Settings {
  return { ...s, touch: { ...s.touch, size: cycle(TOUCH_SIZES, s.touch.size, dir) } };
}

export function stepTouchSteer(s: Settings, dir: -1 | 1): Settings {
  return { ...s, touch: { ...s.touch, steer: cycle(TOUCH_STEERS, s.touch.steer, dir) } };
}

/** One step of the button opacity, clamped. */
export function stepTouchOpacity(s: Settings, dir: -1 | 1): Settings {
  const opacity = Math.max(TOUCH_OPACITY_MIN, Math.min(TOUCH_OPACITY_MAX, s.touch.opacity + dir));
  return { ...s, touch: { ...s.touch, opacity } };
}

export function toggleTouchHand(s: Settings): Settings {
  return { ...s, touch: { ...s.touch, left: !s.touch.left } };
}

const PAD_NAMES = ["✕", "○", "□", "△", "L1", "R1", "L2", "R2", "Select", "Start", "L3", "R3"];

export function padLabel(index: number): string {
  return PAD_NAMES[index] ?? `Button ${index}`;
}

export function keyLabel(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (code.startsWith("Numpad")) return `Num ${code.slice(6)}`;
  return code.replace(/(Left|Right)$/, " $1");
}

export const SETTINGS_KEY = "kickflip.settings";
/** The old Sound On / Off switch (#34), folded into the volume. */
export const MUTED_KEY = "kickflip.muted";

/**
 * Reads saved settings (untrusted: whatever's in storage), keeping every
 * good field and falling back to the default for anything missing or broken.
 */
export function parseSettings(json: unknown): Settings {
  const s = defaultSettings();
  if (!json || typeof json !== "object") return s;
  const o = json as Record<string, unknown>;
  if (typeof o.volume === "number" && Number.isInteger(o.volume)) s.volume = Math.max(0, Math.min(VOLUME_MAX, o.volume));
  for (const k of CHOICE_KEYS) {
    const choices: readonly unknown[] = SETTING_CHOICES[k];
    if (choices.includes(o[k])) (s as unknown as Record<ChoiceKey, unknown>)[k] = o[k];
  }
  const pad = o.pad as Record<string, unknown> | undefined;
  const padOk =
    pad &&
    REMAPPABLE.every((a) => Number.isInteger(pad[a]) && (pad[a] as number) >= 0 && (pad[a] as number) < 32 && !RESERVED_PAD.includes(pad[a] as number)) &&
    new Set(REMAPPABLE.map((a) => pad[a])).size === REMAPPABLE.length;
  if (padOk) for (const a of REMAPPABLE) s.pad[a] = pad[a] as number;
  const keys = o.keys as Record<string, unknown> | undefined;
  const lists = keys ? REMAPPABLE.map((a) => keys[a]) : [];
  const keysOk =
    keys &&
    lists.every((l) => Array.isArray(l) && l.length > 0 && l.every((k) => typeof k === "string" && k.length > 0 && !RESERVED_KEYS.includes(k))) &&
    new Set(lists.flat()).size === lists.flat().length;
  if (keysOk) for (const a of REMAPPABLE) s.keys[a] = [...(keys[a] as string[])];
  const t = o.touch as Record<string, unknown> | undefined;
  if (t && typeof t === "object") {
    if (TOUCH_MODES.includes(t.mode as TouchMode)) s.touch.mode = t.mode as TouchMode;
    if (TOUCH_SIZES.includes(t.size as TouchSize)) s.touch.size = t.size as TouchSize;
    if (typeof t.opacity === "number" && Number.isInteger(t.opacity))
      s.touch.opacity = Math.max(TOUCH_OPACITY_MIN, Math.min(TOUCH_OPACITY_MAX, t.opacity));
    if (typeof t.left === "boolean") s.touch.left = t.left;
    if (TOUCH_STEERS.includes(t.steer as TouchSteer)) s.touch.steer = t.steer as TouchSteer;
  }
  s.cheats = parseCheats(o.cheats);
  s.specials = parseSpecialSeqs(o.specials);
  s.hintsSeen = parseHintsSeen(o.hintsSeen);
  return s;
}

type Store = Pick<Storage, "getItem" | "setItem">;

/** The saved settings, or the defaults; an old "muted" switch turns the volume off. */
export function loadSettings(store: Store | null): Settings {
  try {
    const saved = store?.getItem(SETTINGS_KEY);
    if (saved) return parseSettings(JSON.parse(saved));
    const s = defaultSettings();
    if (store?.getItem(MUTED_KEY) === "1") s.volume = 0;
    return s;
  } catch {
    return defaultSettings();
  }
}

/** Saves settings; false when storage refuses (they still apply this session). */
export function saveSettings(store: Store | null, s: Settings): boolean {
  try {
    if (!store) return false;
    store.setItem(SETTINGS_KEY, JSON.stringify(s));
    return true;
  } catch {
    return false;
  }
}
